/**
 * AttachmentsField — file picker that uploads to /communication/attachments
 * (M1.14 wave 3).
 *
 * Uses the raw fetch path (rather than apiRequest) because the upload
 * endpoint takes multipart/form-data and apiRequest currently only handles
 * JSON. The bearer token is pulled from authStore so the request still
 * authenticates.
 *
 * The 20-attachment cap is enforced client-side via the button's disabled
 * state; the server reject-list is the real backstop.
 */
import type { CommunicationAttachmentStaged } from '@peerless/shared';
import { FileText, Loader2, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { apiRequest } from '../../lib/api-client';
import { formatBytes } from '../../lib/format';


export function AttachmentsField({
  values,
  setValues,
}: {
  values: CommunicationAttachmentStaged[];
  setValues: (v: CommunicationAttachmentStaged[]) => void;
}): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const staged = await apiRequest<CommunicationAttachmentStaged>(
        '/communication/attachments',
        { method: 'POST', body: form },
      );
      setValues([...values, staged]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
        Attachments
      </p>
      <ul className="space-y-1">
        {values.map((a, i) => (
          <li
            key={a.storedPath}
            className="flex items-center justify-between gap-2 rounded-sm border border-border bg-bg/30 px-2 py-1"
          >
            <span className="flex items-center gap-1.5 text-xs text-text">
              <FileText className="size-3 text-muted" />
              {a.filename}{' '}
              <span className="text-muted">({formatBytes(a.sizeBytes)})</span>
            </span>
            <button
              type="button"
              onClick={() => setValues(values.filter((_, idx) => idx !== i))}
              aria-label="Remove attachment"
              className="text-muted hover:text-danger"
            >
              <X className="size-3" />
            </button>
          </li>
        ))}
      </ul>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading || values.length >= 20}
        className="mt-1.5 inline-flex items-center gap-1.5 rounded-sm border border-border bg-surface px-2 py-1 text-[11px] text-muted hover:bg-bg hover:text-text disabled:opacity-50"
      >
        {uploading ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Paperclip className="size-3" />
        )}
        Add attachment
      </button>
      <p className="mt-1 text-[10px] text-muted">Max 25 MB per file. Email channels only.</p>
    </div>
  );
}

